import { ReactNode } from 'react';
import {
	CartesianGrid,
	Label,
	Line,
	LineChart,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	TooltipProps,
	XAxis,
	YAxis,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';

interface Props<T extends { [key: string]: unknown }> {
	width: number;
	height: number;
	data: T[];
	dataKey: DataKey<T>;
	xKey: string;
	xTicks?: (string | number)[];
	xTicksFormatter?: (value: string | number) => string;
	xDomain?: [(input: number) => number, (input: number) => number];
	yTicks?: (string | number)[];
	yDomain?: number[];
	xLabel?: string;
	yLabel?: string;
	tooltip?: (props: TooltipProps<number, string>) => ReactNode;
	referenceLine?: {
		label: string;
		value: number;
	};
	hideReferenceLine?: boolean;
}

export const CustomizableLineChart = <T extends { [key: string]: unknown }>({
	width,
	height,
	data,
	dataKey,
	xKey,
	xTicks,
	xTicksFormatter,
	xDomain,
	xLabel,
	yLabel,
	yTicks,
	yDomain,
	tooltip,
	referenceLine,
	hideReferenceLine,
}: Props<T>) => {
	return (
		<ResponsiveContainer width="100%" minHeight={height} minWidth={width}>
			<LineChart data={data} margin={{ top: 20, bottom: 20, left: 20, right: 20 }}>
				<CartesianGrid stroke="#E9EBED" />
				<XAxis
					dataKey={xKey}
					axisLine={false}
					ticks={xTicks}
					domain={xDomain}
					type="number"
					tickFormatter={xTicksFormatter}
					tickLine={false}
					textAnchor="start"
				>
					<Label value={xLabel} position="bottom" />
				</XAxis>
				<YAxis axisLine={false} tickLine={false} domain={yDomain} ticks={yTicks}>
					<Label
						value={yLabel}
						angle={-90}
						position="left"
						style={{ textAnchor: 'middle' }}
					/>
				</YAxis>
				{referenceLine && !hideReferenceLine && (
					<ReferenceLine x={referenceLine.value} stroke={defaultTheme.v3.colors.blue[4]}>
						<Label value={`TX date: ${referenceLine.label}`} position="inside" />
					</ReferenceLine>
				)}
				<Tooltip content={tooltip} cursor={false} />
				<Line
					type="stepAfter"
					dataKey={dataKey}
					stroke={defaultTheme.v3.colors.blue[6]}
					strokeWidth={2}
					dot={false}
					activeDot={{ width: 6, stroke: 'none' }}
				/>
			</LineChart>
		</ResponsiveContainer>
	);
};
